import { CurrencyEnum } from '../../enums';
import { GenericStatusByCall } from '../../enums/status-by-call';
import {
  SORExtendedHoursMarker,
  SOROrderSide,
  SOROrderStatus,
  SOROrderTimeInForce,
  SOROrderType,
  TenNSOROrderStatus,
} from '../../models/gateway/types';
import { PeopleAlsoTradeItem, ReportingCall } from '../../models/reporting/types';
import { NullableNumber, NullableString } from '../../util/types';
import { CommonPayloadWithCaller } from '../common-actions';
import { SubscribeGroup } from '../market-data/types';

export type GetAllAvailableCashPayload = {
  accountReferenceIds?: string[];
};

export type GetMyAccountChartDataPayload = {
  accountsList: string[];
  fromDate: string;
  toDate: string;
  messageType?: string;
};

export type NetIncomeComponentsChartData = {
  date: string;
  netEquity: number;
  pnlResult: number;
};

export type OrderDetailPayloadData = {
  accountReferenceId: string;
  parentOrderId: string;
};

export type OpenPositionsPayloadData = {
  accountReferenceId?: string;
  accountsList?: string[];
  symbol?: string;
  positionType?: string,
  group?: SubscribeGroup;
  messageType?: string;
} | undefined;

export type OpenPosition = {
  accountReferenceId: string;
  symbol: string;
  currency: string;
  positionType: PositionType;
  lastExecutionDateTime: string;
  quantity: number;
  averageOpeningPrice: number;
  cost: number;
  realizedPnl?: number;
};

export type ReportingOrder = {
  symbol: string;
  parentOrderId: string;
  totalQuantity: number;
  sorExecutedQuantity: number;
  aqxExecutedQuantity: number;
  averageFilledPrice: number;
  orderRequestedPrice: number;
  type: SOROrderType;
  side: SOROrderSide;
  orderDate: string;
  timeInForce: SOROrderTimeInForce;
  expirationDate: string;
};

export type OrderDetailResponse = ReportingOrder & {
  status: SOROrderStatus;
  stopPrice: number;
  fillDate: string;
  lastUpdatedDateTime: string;
  orderValue: number;
  grossRealizedPnl: number;
  regulatoryFees: number;
  tradingSessionId?: string;
  taf?: number;
  sec?: number;
};

export type AccountTradeHistoryPayloadData = {
  accountsList?: string[];
  fromDate?: string;
  toDate?: string;
  symbols_StringList?: string;
  statuses_StringList?: string;
  tenNStatuses_StringList?: string;
  pageNumber?: number;
  rowsPerPage?: number;
  isPendingOrdersCall?: boolean;
  isSymbolDetails?: boolean;
  messageType?: string;
} & CommonPayloadWithCaller;

export type AccountsTradeHistory = {
    accountReferenceId: string;
    ordersList: AccountTradeHistoryResponseData[];
}

export type AccountTradeHistoryResponseDataWithCustomData = {
  accounts: AccountsTradeHistory[];
  customData?: AccountTradeHistoryCustomData;
};

export type AccountTradeHistoryCustomData = {
  isSymbolDetails?: boolean;
  queueLength?: number;
  isPendingOrdersCall?: boolean;
}

export type AccountTradeHistoryResponseData = ReportingOrder & {
  pages: number;
  rowNumber: number;
  orderStopPrice: number;
  aqxLastExecutionDate: string;
  lastUpdatedDate: string;
  status: SOROrderStatus;
  tenNStatus: TenNSOROrderStatus;
  tradingSessionId: SORExtendedHoursMarker;
};

export enum PositionType {
  Buy = 'Buy',
  Sell = 'Sell',
}

export enum TimeInForce {
  Day,
  GTC,
  AtOpening,
  IOC,
  FOK,
  ExtendedDay,
  GoodTillDate,
  AtClosing,
}

export type MyStocksContent = {
  id: string;
  name: string;
  icon: string;
  buttons: string;
  quantity: number;
  marketPrice: string;
  positionType: string;
  purchasePrice: string;
  unrealizedProfit: string;
  unrealizedProfitNumber: NullableNumber;
  subtitle: string | JSX.Element;
};

export type TradeHistoryParsedOrders = {
  aqxLastExecutionDate: string;
  color: string;
  expirationDate: string;
  lastUpdatedDate: string;
  orderDate: string;
  orderValue: string;
  pages: number;
  parentOrderId: string;
  price: string | number;
  quantity: string | number;
  rowNumber: number;
  tenNParsedStatus: string;
  statusEnum: SOROrderStatus;
  sorExecutedQuantity: number;
  symbol: string;
  timeInForce: SOROrderTimeInForce;
  type: string;
};

export interface ReportingState {
  tradeHistoryOrders: TradeHistoryParsedOrders[];
  tradeHistoryNextPageToRead: number;
  pendingOrders: AccountTradeHistoryResponseData[];
  accountOpenPositionsCompleted: boolean;
  openPositions: OpenPosition[];
  orderDetail: OrderDetailResponse;
  orderDetailCompleted: boolean;
  error: RequestError;
  peopleAlsoTradeData: PeopleAlsoTradeItem[];
  statusByCall: GenericStatusByCall<ReportingCall>;
  errorsLog: string[];
  hasMyStocksOrderUpdate: boolean;
  hasOlderTradeThanFourteenDays: boolean;
}

export type GetAccountsAvailableCashRequest = {
  accountsList: string[],
  messageType?: string,
}

export type AvailableCashElement = {
  accountReferenceId: string;
  availableCash: number;
  baseCurrency: CurrencyEnum | '';
  currency: CurrencyEnum | '';
  instaCash: number;
  isArchivingActive: boolean;
  selectedCurrency: CurrencyEnum | null;
}

export type AvailableCash = {
  availableCashMultiCurrency: AvailableCashElement[];
  totalInBaseCurrency: AvailableCashElement;
}

export type FXRates = {
  currencyFrom: CurrencyEnum;
  currencyTo: CurrencyEnum;
  rate: number;
}

export type GetAccountsAvailableCashResponse = {
  availableCash: AvailableCash[];
  fxRates: FXRates[];
};

export type GetOpenPositionsDataResponse = {
  accounts: {
    accountReferenceId: string;
    openPositions: {
      averageOpeningPrice: number;
      cost: number;
      currency: string;
      lastExecutionDateTime: string;
      positionType: string;
      quantity: number;
      realizedPnl: number;
      symbol: string;
    }[];
  }[];
};

export type RequestError = {
  status: NullableNumber;
  hasRequestError: boolean;
};

export type GetAccountTradeHistoryFirstAndLastTradePayload = {
  accountsList: string;
};

export type AccountTradeHistoryFirstAndLastTradeItem = {
  accountReferenceId: string;
  availableCash: number;
  firstTrade: string;
  lastTrade: string;
  baseCurrency: string;
};

export type GetAccountTradeHistoryFirstAndLastTradeResponse = AccountTradeHistoryFirstAndLastTradeItem[];

interface SimplePageableRequest {
  pageNumber: number;
  pageSize: number;
}
